import React from 'react';
import {URLS} from "@/lib/urls";
import RenderIf from "@/components/render-if";
import {Avatar, Tag} from "antd";
import {defaultAvatar} from "@/lib/helper/defaultAvatar";
import {sprintf} from "@/lib/utils/sprintf";
import {Link} from "react-router-dom";

import {
    BookOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    CloseCircleOutlined,
    FileTextOutlined,
    UserOutlined
} from "@ant-design/icons";
import {UserCheck2Icon} from "lucide-react";
import FileViewer from "@/lib/helper/FileViewer";

function HomeworkListItemComponent({homeworkReport}) {

    const statusConfig = {
        pending: {color: 'orange', label: 'در انتظار بررسی', icon: <ClockCircleOutlined/>},
        accepted: {color: 'green', label: 'تایید شده', icon: <CheckCircleOutlined/>},
        rejected: {color: 'red', label: 'رد شده', icon: <CloseCircleOutlined/>},
    };

    function formatDate(iso) {
        const d = new Date(iso);
        return d.toLocaleDateString('fa-IR', {year: 'numeric', month: 'long', day: 'numeric'});
    }

    function getScoreStyles(score, maxScore) {
        if (score > maxScore / 2) {
            return {
                bg: 'bg-green-50 dark:bg-green-950/40',
                text: 'text-green-700 dark:text-green-400',
                border: 'border-green-200 dark:border-green-800'
            };
        }
        if (score > maxScore / 4) {
            return {
                bg: 'bg-yellow-50 dark:bg-yellow-950/40',
                text: 'text-yellow-700 dark:text-yellow-400',
                border: 'border-yellow-200 dark:border-yellow-800'
            };
        }
        return {
            bg: 'bg-red-50 dark:bg-red-950/40',
            text: 'text-red-700 dark:text-red-400',
            border: 'border-red-200 dark:border-red-800'
        };
    }

    const cfg = statusConfig[homeworkReport.status];

    const scoreStyles = getScoreStyles(
        homeworkReport?.score,
        homeworkReport?.homework?.score
    );

    return (
        <div>
            <Link
                to={sprintf(URLS.teacher.homeworks.reportsEdit, {
                    id: homeworkReport.id,
                })}
                className="
                block group
                border dark:border-gray-700 border-gray-200
                rounded-2xl p-4 mb-4
                hover:border-blue-200 dark:hover:border-blue-700
                hover:shadow-md dark:hover:shadow-black/20
                transition-all duration-200
            "
            >
                {/* HEADER */}
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex flex-wrap items-center gap-3 min-w-0">
                        <Avatar
                            src={
                                homeworkReport?.student_detail?.avatar ??
                                defaultAvatar()
                            }
                            size={48}
                            className="
                            flex-shrink-0
                            border dark:border-gray-700 border-blue-100
                            bg-blue-50 dark:bg-blue-950/40
                        "
                        />

                        <div className="min-w-0">
                            <div className="font-bold text-gray-900 dark:text-gray-100 truncate">
                                {homeworkReport?.student_detail?.firstname}{" "}
                                {homeworkReport?.student_detail?.lastname}
                            </div>

                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                                <Tag
                                    icon={cfg.icon}
                                    color={cfg.color}
                                    className="rounded-full m-0"
                                >
                                    {cfg.label}
                                </Tag>

                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                    {formatDate(homeworkReport.created_at)}
                                </span>
                            </div>
                        </div>
                    </div>

                    {!!homeworkReport?.score && (
                        <div
                            className={`
                            ${scoreStyles.bg}
                            ${scoreStyles.text}
                            ${scoreStyles.border}
                            border rounded-xl
                            px-3 py-1
                            text-sm font-bold
                            whitespace-nowrap
                        `}
                        >
                            {homeworkReport?.score}/
                            {homeworkReport?.homework?.score}
                        </div>
                    )}
                </div>

                {/* CONTENT */}
                <div className="mt-4 space-y-3">

                    {/* lesson */}
                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                        <BookOutlined className="text-gray-500 dark:text-gray-400"/>

                        <span className="text-gray-500 dark:text-gray-400">
                            درس:
                        </span>

                        <span className="font-medium text-gray-800 dark:text-gray-200">
                            {homeworkReport.homework?.lesson?.topic?.title}
                        </span>
                    </div>

                    {/* homework title */}
                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                        <FileTextOutlined className="text-gray-500 dark:text-gray-400"/>

                        <span className="text-gray-500 dark:text-gray-400">
                            عنوان تکلیف:
                        </span>

                        <span className="font-medium text-gray-800 dark:text-gray-200 truncate">
                            {homeworkReport.homework?.title}
                        </span>
                    </div>

                    {/* student answer */}
                    <div className="
                    rounded-xl
                    border dark:border-gray-700 border-gray-200
                    bg-gray-50 dark:bg-gray-800/60
                    p-3
                ">
                        <div className="
                        flex items-center gap-2
                        text-sm font-semibold
                        text-gray-800 dark:text-gray-200
                        mb-2
                    ">
                            <UserOutlined/>
                            پاسخ دانش‌آموز
                        </div>

                        <div className="
                        text-sm leading-7
                        text-gray-700 dark:text-gray-300
                        whitespace-pre-line
                    ">
                            <RenderIf condition={homeworkReport?.file}>
                                <FileViewer fileUrl={homeworkReport?.file}/>
                            </RenderIf>
                            {homeworkReport?.report}
                        </div>
                    </div>

                    {/* teacher advice */}
                    <RenderIf condition={homeworkReport?.advice}>
                        <div className="
                        rounded-xl
                        border dark:border-blue-900/70 border-blue-200
                        bg-blue-50 dark:bg-blue-950/30
                        p-3
                    ">
                            <div className="
                            flex items-center gap-2
                            text-sm font-semibold
                            text-blue-800 dark:text-blue-300
                            mb-2
                        ">
                                <UserCheck2Icon size={16}/>
                                پاسخ مربی
                            </div>

                            <div className="
                            text-sm leading-7
                            text-gray-700 dark:text-gray-300
                            whitespace-pre-line
                        ">
                                {homeworkReport?.advice}
                            </div>
                        </div>
                    </RenderIf>
                </div>
            </Link>
        </div>
    );
}

export default HomeworkListItemComponent;